import { useNavigate } from "@tanstack/react-router";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useTableDisplayState } from "@/components/DataTable";
import type { SortOrder } from "@/features/it-operation-core";
import type { operations } from "@/lib/api/schema";
import { usePaginationSearch, useSortingSearch } from "@/lib/listPage";

import { useCertificationsColumns } from "../-columns";
import { useCertificationsQuery } from "../-query";
import { OrderBy } from "../-zod-schema";
import { EducationAndQualificationType } from "../../-zod-schema";

import { useCertificationsSearch } from "./useCertificationsSearch";

export const useCertificationsTable = (personId?: string) => {
  const { search } = useCertificationsSearch();
  const navigate = useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search: {
      ...search,
      pageIndex: Math.max(0, (search.page || 1) - 1),
      pageSize: search.pageSize ?? 10,
    },
    onSearchChange: (paginationSearch) => {
      const updatedSearch = {
        ...paginationSearch,
        page: Math.max(1, (paginationSearch.pageIndex || 0) + 1),
      };
      navigate({ to: ".", search: { ...search, ...updatedSearch } });
    },
  });

  const convertToApiColumn = (
    column: string | undefined,
  ): NonNullable<
    operations["PersonCertificationController_getPersonCertifications_v2"]["parameters"]["query"]
  >["orderBy"] => {
    if (!column) return undefined;
    switch (column) {
      case "certificationName":
        return OrderBy.CERTIFICATION_NAME;
      case "certificationNumber":
        return OrderBy.CERTIFICATION_NUMBER;
      case "lastRenewalDate":
        return OrderBy.LAST_RENEWAL_DATE;
      case "validTo":
        return OrderBy.VALID_TO;
      case "renewalDeadline":
        return OrderBy.RENEWAL_DEADLINE;
      default:
        return undefined;
    }
  };

  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: (sortingSearch: Record<string, unknown>) =>
      navigate({ to: ".", search: { ...search, ...sortingSearch } }),
  });

  const columns = useCertificationsColumns();

  const apiParams: NonNullable<
    operations["PersonCertificationController_getPersonCertifications_v2"]["parameters"]
  > = {
    query: {
      freeText: search.freeText ?? null,
      page: search.page || 1,
      pageSize: search.pageSize,
      orderBy: convertToApiColumn(search.sortColumn),
      orderDirection: search.sortOrder as SortOrder,
      isArchived:
        search.educationAndQualificationType ===
        EducationAndQualificationType.ARCHIVED
          ? true
          : false,
    },
    path: {
      personId: personId ?? "",
    },
  };

  const { data, isPending } = useCertificationsQuery(apiParams);

  const table = useReactTable({
    data: data?.items ?? [],
    pageCount: data?.maxPage,
    columns: columns,
    state: { pagination, sorting },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onPaginationChange: setPagination,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.subRows,
    onSortingChange: setSorting,
    meta: {
      getRowClassName: (row) => (row.depth > 0 ? "bg-stone-100" : ""),
    },
    manualPagination: true,
    manualSorting: true,
  });

  const displayState = useTableDisplayState(isPending, data?.items ?? []);
  const count = data?.count;

  return { table, displayState, count };
};
